import { IsUUID } from 'class-validator';
import type { VisitFix, VisitStatus } from './visits.dto';

/**
 * On-duty live tracking — officer phones report, supervisors watch.
 *
 * Privacy is the design, not a feature on top of it:
 * - Off unless the municipality turns it on (`liveTrackingEnabled`, default
 *   false), and the officer chooses when to go on duty.
 * - Positions are reported only inside an open duty session, swept after the
 *   municipality's retention period, and readable only with `tracking:view`.
 * - None of these shapes is served by the public API or the Client Portal.
 */

/** How the phone samples while on duty. Served by the API so it can be tuned centrally. */
export const TRACKING_CONFIG = {
  /** Minimum movement before a new point is taken. */
  distanceMeters: 25,
  /** Minimum time between points. */
  intervalSeconds: 45,
  /** A marker older than this is shown as stale (greyed) on the live map. */
  staleAfterSeconds: 300,
  /** Most points one upload may carry; a longer offline backlog is sent in turns. */
  maxBatch: 100,
  /** Uploads per officer per minute before the API answers 429. */
  maxUploadsPerMinute: 12,
} as const;

export type TrackingConfig = typeof TRACKING_CONFIG;

/** Error codes the API answers a refused upload with; the phone stops tracking on either. */
export type TrackingRefusal = 'tracking_disabled' | 'not_on_duty';

export type DutyEndReason = 'officer' | 'logout' | 'disabled' | 'stale';

export interface TrackingMunicipality {
  localGovernmentId: string;
  name: string;
  /** Whether the municipality has live tracking turned on. */
  enabled: boolean;
}

export interface DutySession {
  id: string;
  localGovernmentId: string;
  startedAt: string;
}

/** `GET /tracking/me` — what the phone needs to decide whether to offer On duty. */
export interface TrackingStatus {
  municipalities: TrackingMunicipality[];
  duty: DutySession | null;
  config: TrackingConfig;
}

/** `POST /tracking/duty`. */
export class StartDutyDto {
  @IsUUID()
  localGovernmentId!: string;
}

/**
 * One point in a `POST /me/location` batch. The validating class lives in
 * gov-api: nested validation needs class-transformer's `@Type`, which reads
 * reflect metadata at import time, and this lib is also loaded by the browser
 * and the phone where that metadata does not exist.
 */
export interface PositionPoint {
  lat: number;
  lng: number;
  accuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
  /** The device clock at the fix, ISO 8601. */
  recordedAt: string;
}

/** `POST /me/location` — a batch of points from the phone's queue. */
export interface ReportPositionsBody {
  points: PositionPoint[];
}

export interface ReportPositionsResult {
  accepted: number;
  /** Points dropped as implausible (future-dated, or before the session began). */
  rejected: number;
}

/** The visit an officer is on, as the supervisor's map shows it. */
export interface OfficerVisitSummary {
  visitId: string;
  fileId: string;
  reference: string;
  address: string;
  status: VisitStatus;
  destination: { lat: number; lng: number } | null;
  /** When the planned route said they would arrive. */
  plannedEta: string | null;
  /** True when the ETA is a straight-line estimate, not a road route. */
  routeEstimated: boolean | null;
}

/** One officer on the live map. */
export interface OfficerPositionView {
  officerId: string;
  officerName: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  heading: number | null;
  speed: number | null;
  recordedAt: string;
  dutyStartedAt: string;
  visit: OfficerVisitSummary | null;
}

/** Realtime `officer.position`, to the municipality's supervisors room only. */
export interface OfficerPositionPayload {
  localGovernmentId: string;
  officer: OfficerPositionView;
}

/** Realtime `officer.duty` — an officer went on or off duty. */
export interface OfficerDutyPayload {
  localGovernmentId: string;
  officerId: string;
  onDuty: boolean;
}

/** An officer with positions inside the retention window — the replay picker. */
export interface TrackedOfficer {
  officerId: string;
  officerName: string;
  lastRecordedAt: string;
}

export interface TrackPoint {
  lat: number;
  lng: number;
  accuracy: number | null;
  recordedAt: string;
}

export interface TrackVisitMarker {
  visitId: string;
  fileId: string;
  reference: string;
  address: string;
  status: VisitStatus;
  onSite: VisitFix | null;
  completed: VisitFix | null;
}

/** `GET /tracking/local-governments/:id/officers/:officerId/track?date=`. */
export interface TrackHistory {
  officerId: string;
  officerName: string;
  /** `YYYY-MM-DD`, in the enforcement time zone. */
  date: string;
  retentionDays: number;
  points: TrackPoint[];
  visits: TrackVisitMarker[];
}
